import React, { useContext, useMemo } from 'react';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { useCompany, useSalesOrderConfig } from '../../../utils/FetchConfig';
import { useFullUserDetails } from '../../../utils/hooks';
import productDropdown from '../../../utils/GridDropdownSetup';
import { IEditableDataGridColumnDef, IEditableDataGridDropdownOptions } from '../../../@types/editableDataGridTypes';
import DropdownEditorClean from '../../../components/DropdownEditor/DropdownEditorClean';
import { checkLineCanProductChange, isFullyInvoiced } from '../../../utils/LineUtils';
import getProductDP from '../../../utils/getProductDP';
import DropdownEditor from '../../../components/DropdownEditor';
import { PriceOption } from '../../../components/CommonOrderComponents/PriceOption';
import {
  grossPriceCreatePrompt,
  grossPriceOptionCreator,
  roundByPrecision,
} from '../../../components/CommonOrderComponents/CommonOrderFunctions';
import { NumberRange } from '../../../@types/numberRange';
import Formatters from '../../../components/grid-addons/formatters';
import {
  FilterOperation,
  ISalesOrder,
  ISalesOrderLine,
  Maybe,
  PriceSelectionPermission,
  SalesOrderActivationType,
  SalesOrderLineStatus,
} from '../../../graphql/schema/p4types';
import DateInputEditor from '../../../components/DateInputEditor/DateInputEditor';
import ScheduleFormModalFormatter from './ScheduleFormModalFormatter';

export const useSalesOrderLineColumns = ({
  currencyCode,
  setProductModalState,
  match,
}: {
  currencyCode?: Maybe<string>;
  setProductModalState: React.Dispatch<React.SetStateAction<{ modalOpen: boolean }>>;
  match: any;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const order = connectedForm.values as ISalesOrder;
  const orderConfig = useSalesOrderConfig();
  const fullUserDetails = useFullUserDetails();
  const company = useCompany();

  return useMemo(() => {
    const getSalesProductLookupData = () => {
      return {
        customer: order.customer,
      };
    };

    //Building up dropdown this way to avoid mistakes like..Overwriting the data against the entity which would affect 'all' dropdowns using products.
    const configuredProductDropdown = productDropdown(orderConfig);
    const productOptions: IEditableDataGridDropdownOptions = {
      editor: <DropdownEditorClean />,
      dropdown: {
        ...configuredProductDropdown.dropdown,
        clearable: false,
        selectModal: 'SalesProductLookup',
        selectModalData: getSalesProductLookupData,
        selectModalUpdateOpen: (newState) => {
          setProductModalState({ modalOpen: newState });
        },
        predefinedFilters: [
          {
            filterOperation: FilterOperation.Custom,
            filterParameters: {
              field: 'template',
            },
          },
        ],
        enableMulti: true,
        temporaryOnClick: true,
      },
      searchTabOffset: configuredProductDropdown.searchTabOffset,
      searchEnterOffset: configuredProductDropdown.searchEnterOffset,
    };

    const productColumn: IEditableDataGridColumnDef = {
      name: 'Product',
      key: 'product',
      editable: checkLineCanProductChange,
      editor: <DropdownEditorClean />,
      options: productOptions,
      formatter: 'dropdown',
      widthFactor: 2.3,
      resizable: false,
      temporaryOnChange: true,
      useAlternate: true,
      viewControllerMandatory: true,
    };

    const descriptionColumn: IEditableDataGridColumnDef = {
      name: 'Description',
      key: 'description',
      editable: true,
      resizable: true,
      widthFactor: 2.3, //Only in comparison to other freely scaling columns.
    };

    const quantityColumn: IEditableDataGridColumnDef = {
      name: 'Quantity',
      key: 'quantity',
      formatter: 'numeric',
      editable: (salesOrderLine) => {
        return !isFullyInvoiced(salesOrderLine);
      },
      resizable: true,
      options: {
        format: { decimals: getProductDP },
      },
      temporaryOnChange: true,
      viewControllerMandatory: true,
    };

    const scheduleColumn: IEditableDataGridColumnDef = {
      name: 'Schedule',
      key: '',
      editable: false,
      resizable: true,
      formatter: ScheduleFormModalFormatter,
    };

    const unitColumn: IEditableDataGridColumnDef = {
      name: 'Unit',
      key: 'activeSellingUnit',
      editor: <DropdownEditor />,
      options: {
        dropdown: {
          labelKey: 'description',
          valueKey: 'description',
          options: 'potentialUnits',
        },
      },
      editable: (rowData) => {
        return (
          !rowData.complete &&
          !isFullyInvoiced(rowData) &&
          !rowData?.salesOrderLineStatus?.includes(SalesOrderLineStatus.Dispatched)
        );
      },
      resizable: true,
      formatter: 'dropdown',
      temporaryOnChange: true,
    };

    const priceSelectionPermission =
      fullUserDetails?.companyUser?.priceSelectionPermission ?? PriceSelectionPermission.None;

    let createable = false;
    let editable = false;

    if (
      priceSelectionPermission === PriceSelectionPermission.Standard ||
      priceSelectionPermission === PriceSelectionPermission.Restricted ||
      priceSelectionPermission === PriceSelectionPermission.RestrictedWithManual ||
      priceSelectionPermission === PriceSelectionPermission.Manual
    ) {
      editable = true;
    }
    if (
      priceSelectionPermission === PriceSelectionPermission.Manual ||
      priceSelectionPermission === PriceSelectionPermission.RestrictedWithManual
    ) {
      createable = true;
    }

    const discountEditable = (salesOrderLine: ISalesOrderLine) => {
      return !isFullyInvoiced(salesOrderLine) && createable;
    };

    const sellingPricePrecision = company?.sellingPricePrecision ?? 2;

    const sellingPriceColumns: Array<IEditableDataGridColumnDef<ISalesOrderLine>> = [
      {
        name: `Selling Price ${currencyCode ? `(${currencyCode})` : ''}`,
        key: 'grossSellingPrice',
        editor: <DropdownEditorClean />,
        options: {
          format: {
            decimals: sellingPricePrecision,
          },
          dropdown: {
            labelKey: 'nettPrice',
            valueKey: 'nettPrice',
            selectionKey: 'grossPrice',
            rowSelectionKeys: ['termA', 'termB', 'termC'],
            options: 'potentialPrices',
            optionRenderer: PriceOption,
            creatable: createable,
            promptTextCreator: grossPriceCreatePrompt,
            newOptionCreator: grossPriceOptionCreator,
            useLatestReactSelect: true,
            getGroupLabel: (group) =>
              (group.options.length > 0 && group.options[0].priceOriginDescription) || 'Unknown price group',
            formatNewValue: (value) => roundByPrecision(value, sellingPricePrecision),
          },
        },
        formatter: 'numeric',
        editable: (salesOrderLine) => {
          return !isFullyInvoiced(salesOrderLine) && editable;
        },
        resizable: true,
        temporaryOnChange: true,
      },
      {
        name: 'Discount %',
        key: 'discountPercentage',
        formatter: 'numeric',
        resizable: false,
        editable: discountEditable,
        options: {
          format: {
            decimals: 2,
          },
        },
        temporaryOnChange: true,
      },
      {
        name: 'Margin %',
        key: 'displayProfitMargin',
        formatter: 'numeric',
        editable: discountEditable,
        temporaryOnChange: true,
        options: {
          format: {
            decimals: 2,
          },
        },
        viewControllerOptIn: true,
      },
      {
        name: 'Margin',
        key: `displayProfitValue`,
        formatter: 'numeric',
        editable: discountEditable,
        temporaryOnChange: true,
        cellTooltip: (rowData: ISalesOrderLine) => {
          if (!rowData?.sellingUnitQuantity) {
            return null;
          }
          return (
            <div>
              per {rowData.sellingUnitQuantity} ({rowData.sellingUnitDescription})
            </div>
          );
        },
        viewControllerOptIn: true,
      },
      {
        name: `Nett Price ${currencyCode ? `(${currencyCode})` : ''}`,
        key: 'nettSellingPrice',
        options: {
          format: {
            decimals: sellingPricePrecision,
          },
        },
        formatter: 'numeric',
        editable: discountEditable,
        temporaryOnChange: true,
        resizable: true,
      },
    ];

    const historyColumns: Array<IEditableDataGridColumnDef> = [];

    for (let i: NumberRange<1, 5> = 5; i >= 1; i--) {
      historyColumns.push({
        name: order[`historyTitle${i as NumberRange<1, 5>}`]!,
        key: `history${i}`,
        editable: false,
        resizable: true,
        widthFactor: 0.6, //Only in comparison to other freely scaling columns.
      });
    }

    historyColumns.push({
      name: 'Average Order',
      key: `historyAverage`,
      editable: false,
      resizable: true,
      widthFactor: 0.8,
    });

    historyColumns.push({
      name: 'Total Order',
      key: `historyTotal`,
      editable: false,
      resizable: true,
      widthFactor: 0.8,
    });

    if (match?.url?.startsWith('/Sales/Order/Template')) {
      return [productColumn, descriptionColumn, ...historyColumns, unitColumn, ...sellingPriceColumns, quantityColumn];
    }

    const columns: Array<IEditableDataGridColumnDef> = [
      productColumn,
      descriptionColumn,
      quantityColumn,
      ...(order.salesOrderActivationType === SalesOrderActivationType.Scheduled ? [scheduleColumn] : []),
      unitColumn,
      {
        name: 'Dispatch Group Code',
        key: 'dispatchGroupCode',
        editable: true,
        resizable: true,
        visible: !!order?.showDispatchGroupCode,
      },
      {
        name: `Date Required`,
        key: 'targetDate',
        editable: (salesOrderLine) => {
          return !isFullyInvoiced(salesOrderLine);
        },
        resizable: true,
        editor: DateInputEditor,
        formatter: Formatters.DateFormatter,
        viewControllerOptIn: true,
      },
      ...sellingPriceColumns,
      {
        name: `Line Value ${currencyCode ? `(${currencyCode})` : ''}`,
        key: 'nettLineValue',
        editable: false,
        resizable: true,
        formatter: 'numeric',
      },
    ];

    return columns;
  }, [
    company?.sellingPricePrecision,
    currencyCode,
    fullUserDetails?.companyUser?.priceSelectionPermission,
    match?.url,
    order,
    orderConfig,
    setProductModalState,
  ]);
};
